import { Button } from '@/components/common/Button';
import { Form, FormControl, FormField, FormItem } from '@/components/common/Form';
import { InputOTP, InputOTPSlot } from '@/components/common/Input';
import yup from '@/services/yup';
import { yupResolver } from '@hookform/resolvers/yup';
import classNames from 'classnames';
import { observer } from 'mobx-react-lite';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import CountDownClock from '../../components/common/CountDownClock';
import { useStore } from '@/hooks/useStore';
import { flowResult } from 'mobx';
import { DURATION_RESEND_OTP, ROUTES } from '@/configs/constants';
import { toastify } from '@/utils/toastify';

export default observer(function VerifyOtpPage() {
    // hooks
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const uuid = searchParams.get('uuid') ?? '';

    // store
    const { authStore: { resendOtp, verifyOtp } } = useStore();

    // validate schema
    const validateSchema = yup.object().shape({
        verifyOtp: yup
            .string()
            .min(6)
            .required()
    });

    type FormData = yup.InferType<typeof validateSchema>;

    // state
    const form = useForm<FormData>({
        resolver: yupResolver(validateSchema),
        mode: 'onChange',
        defaultValues: {
            verifyOtp: ''
        }
    });

    const [runTimmer, setRunTimer] = useState<boolean>(true);

    // life cycle
    // function
    const onSubmit = async (data: FormData) => {
        const res = await flowResult(verifyOtp({ uuid, ...data }));
        if (res && res.ok) {
            toastify('success', t('messages.verify_otp_success'));
            navigate(`${ROUTES.resetPassword.href}?uuid=${encodeURIComponent(uuid)}`);
        }
    };

    const onClickResendOtp = async () => {
        setRunTimer(true);
        const res = await flowResult(resendOtp({ uuid }));
        if (res && res.ok) {
            toastify('success', t('messages.resend_otp_succes'));
            navigate(`${ROUTES.verifyOtp.href}?uuid=${res.data.uuid}`);
        }
    };

    const handleClickBackToSignin = () => {
        navigate(ROUTES.signIn.href);
    };

    return (
        <div className='flex w-full px-12 flex-col py-6 rounded-xl border bg-card text-card-foreground shadow'>
            <h3 className='title-auth-form text-center'>{t('words_title.verify')}</h3>
            <div className='mt-6 flex flex-col items-center gap-2 text-[0.8125rem] font-medium leading-6'>
                <div>{t('sentences.verify_otp_fpw_title')}</div>
                <div>{t('sentences.verify_otp_fpw_desc')}</div>
            </div>
            <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className='mt-4'>
                    <div className='flex justify-center'>
                        <FormField
                            control={form.control}
                            name='verifyOtp'
                            render={({ field }) => (
                                <FormItem>
                                    <FormControl>
                                        <InputOTP maxLength={6} {...field}>
                                            <InputOTPSlot index={0} />
                                            <InputOTPSlot index={1} />
                                            <InputOTPSlot index={2} />
                                            <InputOTPSlot index={3} />
                                            <InputOTPSlot index={4} />
                                            <InputOTPSlot index={5} />
                                        </InputOTP>
                                    </FormControl>
                                </FormItem>
                            )}
                        />
                    </div>
                    <div className='flex flex-row items-center justify-center gap-1 text-[0.8125rem] font-medium leading-6 mt-4'>
                        <span className='text-text-4'>{t('sentences.verify_otp_resend_desc')}</span>
                        <button type='button' className={classNames('text-link-1', runTimmer && 'opacity-50')} disabled={runTimmer} onClick={onClickResendOtp}>{t('sentences.verify_otp_resend_text')}</button>
                        {
                            runTimmer && (
                                <CountDownClock
                                    duration={DURATION_RESEND_OTP}
                                    callbackWhenZero={() => {
                                        setRunTimer(false);
                                    }}
                                />
                            )
                        }
                    </div>
                    <div className='flex justify-center items-center'>
                        <Button type='submit' variant='submit' className='mt-4 min-w-60' disabled={!form.getFieldState('verifyOtp').isDirty || form.getFieldState('verifyOtp').invalid}>
                            {t('button.verify_otp_code')}
                        </Button>
                    </div>
                    <div className='text-center mt-4 underline text-sm cursor-pointer text-link-1' onClick={handleClickBackToSignin}>
                        {t('button.back_to_signin')}
                    </div>
                </form>
            </Form>
        </div>
    );
});
